import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  removeUserFromGroup,
  deleteGroupPost,
  updateGroup,
  getGroupPosts,
  getGroupPostComments,
  deleteGroupComment
} from '../../services/firestoreService';
import { handleError, handleSuccess, handleWarning } from '../../services/errorHandlingService';

const GroupAdmin = ({ group }) => {
  const { hasRole, currentUser } = useAuth();

  // Always call hooks first, before any conditional logic
  const [activeTab, setActiveTab] = useState('members'); // 'members', 'content', 'settings'
  const [searchTerm, setSearchTerm] = useState('');
  const [contentFilter, setContentFilter] = useState('all'); // 'all', 'discussions', 'comments', 'flagged'

  // Real content data
  const [groupPosts, setGroupPosts] = useState([]);
  const [groupComments, setGroupComments] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);

  // Load real content data when content tab is active
  useEffect(() => {
    if (activeTab === 'content' && group?.id) {
      loadGroupContent();
    }
  }, [activeTab, group?.id]);

  const loadGroupContent = async () => {
    try {
      setContentLoading(true);
      console.log('🔄 Loading group content for admin monitoring...');

      // Load all posts for this group
      const posts = await getGroupPosts(group.id);
      setGroupPosts(posts);

      // Load all comments for all posts
      const allComments = [];
      for (const post of posts) {
        try {
          const comments = await getGroupPostComments(post.id);
          // Add post reference to each comment
          const commentsWithPostRef = comments.map(comment => ({
            ...comment,
            parentPostId: post.id,
            parentPostTitle: post.title
          }));
          allComments.push(...commentsWithPostRef);
        } catch (error) {
          console.error('Error loading comments for post:', post.id, error);
        }
      }
      setGroupComments(allComments);

      console.log('✅ Group content loaded:', {
        posts: posts.length,
        comments: allComments.length
      });
    } catch (error) {
      console.error('❌ Error loading group content:', error);
      handleError(error, {
        context: {
          component: 'GroupAdmin',
          action: 'loadGroupContent',
          groupId: group.id
        }
      });
    } finally {
      setContentLoading(false);
    }
  };

  // Real members derived from the group's member uids
  const [members, setMembers] = useState(
    (group?.members || []).map((uid) => ({
      id: uid,
      role: group?.createdBy === uid ? 'owner' : 'member'
    }))
  );

  // Keep members in sync if the group prop changes
  useEffect(() => {
    setMembers(
      (group?.members || []).map((uid) => ({
        id: uid,
        role: group?.createdBy === uid ? 'owner' : 'member'
      }))
    );
  }, [group?.members, group?.createdBy]);

  // Local moderator uid list so promote/demote reflects immediately in the UI.
  const [moderators, setModerators] = useState(group?.moderators || []);
  useEffect(() => { setModerators(group?.moderators || []); }, [group?.moderators]);

  // Controlled settings form state, seeded from the group
  const [settingsForm, setSettingsForm] = useState({
    name: group?.name || '',
    description: group?.description || '',
    category: group?.category || 'Industry'
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Re-seed settings form when the group prop changes
  useEffect(() => {
    setSettingsForm({
      name: group?.name || '',
      description: group?.description || '',
      category: group?.category || 'Industry'
    });
  }, [group?.name, group?.description, group?.category]);

  // Save group settings
  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      await updateGroup(group.id, {
        name: settingsForm.name,
        description: settingsForm.description,
        category: settingsForm.category
      });
      handleSuccess('Group settings saved.');
    } catch (error) {
      handleError(error, {
        context: {
          component: 'GroupAdmin',
          action: 'saveSettings',
          groupId: group.id
        }
      });
    } finally {
      setSavingSettings(false);
    }
  };

  // Combine posts and comments for content moderation
  const content = [
    // Add posts as discussions
    ...groupPosts.map(post => ({
      id: `post-${post.id}`,
      type: 'discussion',
      title: post.title,
      content: post.content,
      author: post.authorName || 'Unknown',
      createdAt: post.createdAt,
      flagged: false, // TODO: Add flagging system
      originalId: post.id,
      originalType: 'post'
    })),
    // Add comments
    ...groupComments.map(comment => ({
      id: `comment-${comment.id}`,
      type: 'comment',
      content: comment.content,
      author: comment.authorName || 'Unknown',
      createdAt: comment.createdAt,
      parentTitle: comment.parentPostTitle || 'Unknown Discussion',
      flagged: false, // TODO: Add flagging system
      originalId: comment.id,
      originalType: 'comment'
    }))
  ];

  // Check if user is admin or group moderator (after all hooks)
  const isAdmin = hasRole('admin') || group?.moderators?.includes(currentUser?.uid);

  // If not admin, don't render anything
  if (!isAdmin) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  // Filter members based on search (we only have uids, so match against the id)
  const filteredMembers = members.filter(member =>
    String(member.id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter content based on filter and search
  const filteredContent = content.filter(item => {
    const matchesSearch = 
      (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      item.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.author.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      contentFilter === 'all' || 
      (contentFilter === 'flagged' && item.flagged) ||
      (contentFilter === item.type);
    
    return matchesSearch && matchesFilter;
  });

  // Handle member role change
  const handleRoleChange = (memberId, newRole) => {
    setMembers(members.map(member => 
      member.id === memberId ? { ...member, role: newRole } : member
    ));
  };

  // Handle member removal
  const handleRemoveMember = async (memberId) => {
    if (!window.confirm(`Are you sure you want to remove this member from the group? This action cannot be undone.`)) {
      return;
    }

    try {
      await removeUserFromGroup(group.id, memberId);
      setMembers(members.filter(member => member.id !== memberId));
      handleSuccess(`Member has been removed from the group.`);
    } catch (error) {
      handleError(error, {
        context: {
          component: 'GroupAdmin',
          action: 'removeMember',
          groupId: group.id,
          memberId
        }
      });
    }
  };

  // Promote a member to / demote from group moderator. Only the group creator or
  // a platform admin may change moderators (Firestore rules enforce this too).
  const canManageModerators = hasRole('admin') || group?.createdBy === currentUser?.uid;
  const handleToggleModerator = async (memberId) => {
    const isMod = moderators.includes(memberId);
    const next = isMod ? moderators.filter((m) => m !== memberId) : [...moderators, memberId];
    try {
      await updateGroup(group.id, { moderators: next });
      setModerators(next);
      handleSuccess(isMod ? 'Moderator removed.' : 'Member promoted to moderator.');
    } catch (error) {
      handleError(error, { context: { component: 'GroupAdmin', action: 'toggleModerator', groupId: group.id, memberId } });
    }
  };

  // Handle content moderation (flagging)
  const handleFlagContent = (contentId) => {
    // TODO: Implement flagging system in Firebase
    // For now, just show a message
    handleWarning('Flagging system coming soon. Use delete for now to remove inappropriate content.');
  };

  // Handle content deletion
  const handleDeleteContent = async (item) => {
    const contentTitle = item.title || item.content.substring(0, 50) + '...';
    if (!window.confirm(`Are you sure you want to delete "${contentTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      if (item.originalType === 'post') {
        await deleteGroupPost(item.originalId);
        // Remove post and its comments from local state
        setGroupPosts(prev => prev.filter(post => post.id !== item.originalId));
        setGroupComments(prev => prev.filter(comment => comment.parentPostId !== item.originalId));
      } else if (item.originalType === 'comment') {
        await deleteGroupComment(item.originalId, item.parentPostId);
        // Remove comment from local state
        setGroupComments(prev => prev.filter(comment => comment.id !== item.originalId));
      }

      handleSuccess('Content deleted successfully.');
    } catch (error) {
      handleError(error, {
        context: {
          component: 'GroupAdmin',
          action: 'deleteContent',
          groupId: group.id,
          contentId: item.originalId,
          contentType: item.originalType
        }
      });
    }
  };

  return (
    <div>
      {/* Admin Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab('members')}
            className={`py-4 px-6 font-medium text-sm border-b-2 ${
              activeTab === 'members'
                ? 'border-apn-blue text-apn-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Members
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`py-4 px-6 font-medium text-sm border-b-2 ${
              activeTab === 'content'
                ? 'border-apn-blue text-apn-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Content Moderation
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-6 font-medium text-sm border-b-2 ${
              activeTab === 'settings'
                ? 'border-apn-blue text-apn-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Group Settings
          </button>
        </nav>
      </div>

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div>
          <div className="mb-6">
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMembers.map((member) => (
                  <tr key={member.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {String(member.id).charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{member.role === 'owner' ? 'Owner' : moderators.includes(member.id) ? 'Moderator' : 'Member'}</div>
                          <div className="text-sm text-gray-500 break-all">{member.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(member.id, e.target.value)}
                        disabled={member.role === 'owner'}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-apn-blue focus:border-apn-blue sm:text-sm"
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                        <option value="owner">Owner</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      —
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                      {canManageModerators && member.role !== 'owner' && (
                        <button
                          onClick={() => handleToggleModerator(member.id)}
                          className="text-apn-blue hover:text-apn-dark-blue"
                        >
                          {moderators.includes(member.id) ? 'Remove moderator' : 'Make moderator'}
                        </button>
                      )}
                      {member.role !== 'owner' && (
                        <button
                          onClick={() => handleRemoveMember(member.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredMembers.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-sm text-gray-500">
                      No members to display yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Content Moderation Tab */}
      {activeTab === 'content' && (
        <div>
          <div className="mb-6 flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
            <input
              type="text"
              placeholder="Search content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-2/3 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
            />
            <select
              value={contentFilter}
              onChange={(e) => setContentFilter(e.target.value)}
              className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
            >
              <option value="all">All Content</option>
              <option value="discussions">Discussions</option>
              <option value="comment">Comments</option>
              <option value="flagged">Flagged Content</option>
            </select>
          </div>

          {contentLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-apn-blue mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading group content...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredContent.map((item) => (
                <div key={item.id} className={`p-4 border rounded-lg ${item.flagged ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                  <div className="flex justify-between">
                    <div className="flex-grow">
                      <div className="flex items-center mb-2">
                        <span className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded mr-2">
                          {item.type === 'discussion' ? 'Discussion' : 'Comment'}
                        </span>
                        <span className="text-gray-500 text-sm">by {item.author}</span>
                        <span className="mx-2 text-gray-400">•</span>
                        <span className="text-gray-500 text-sm">
                          {item.createdAt?.toDate ?
                            item.createdAt.toDate().toLocaleString() :
                            new Date(item.createdAt).toLocaleString()
                          }
                        </span>
                      </div>
                      {item.type === 'discussion' && (
                        <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                      )}
                      {item.type === 'comment' && (
                        <div className="text-sm text-gray-500 mb-2">
                          On: {item.parentTitle}
                        </div>
                      )}
                      <p className="text-gray-700">{item.content}</p>
                    </div>
                    <div className="flex flex-col space-y-2 ml-4">
                      <button
                        onClick={() => handleFlagContent(item.id)}
                        className={`text-sm px-3 py-1 rounded ${
                          item.flagged
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title="Flag/Unflag content for review"
                      >
                        {item.flagged ? 'Unflag' : 'Flag'}
                      </button>
                      <button
                        onClick={() => handleDeleteContent(item)}
                        className="text-sm px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                        title="Permanently delete this content"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {filteredContent.length === 0 && !contentLoading && (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    {content.length === 0
                      ? 'No content found in this group.'
                      : 'No content found matching your criteria.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Group Information</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="group-name" className="block text-sm font-medium text-gray-700">
                    Group Name
                  </label>
                  <input
                    type="text"
                    id="group-name"
                    value={settingsForm.name}
                    onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-apn-blue focus:border-apn-blue sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="group-description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    id="group-description"
                    rows="3"
                    value={settingsForm.description}
                    onChange={(e) => setSettingsForm({ ...settingsForm, description: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-apn-blue focus:border-apn-blue sm:text-sm"
                  ></textarea>
                </div>
                <div>
                  <label htmlFor="group-category" className="block text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <select
                    id="group-category"
                    value={settingsForm.category}
                    onChange={(e) => setSettingsForm({ ...settingsForm, category: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-apn-blue focus:border-apn-blue sm:text-sm"
                  >
                    <option value="Industry">Industry</option>
                    <option value="Topic">Topic</option>
                    <option value="Location">Location</option>
                    <option value="Interest">Interest</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Privacy Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    id="private-group"
                    name="privacy"
                    type="radio"
                    defaultChecked
                    className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300"
                  />
                  <label htmlFor="private-group" className="ml-3 block text-sm font-medium text-gray-700">
                    Private - Only members can see content
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="public-group"
                    name="privacy"
                    type="radio"
                    className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300"
                  />
                  <label htmlFor="public-group" className="ml-3 block text-sm font-medium text-gray-700">
                    Public - Anyone can see content, but only members can post
                  </label>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Moderation Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    id="auto-approve"
                    type="checkbox"
                    defaultChecked
                    className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                  />
                  <label htmlFor="auto-approve" className="ml-3 block text-sm font-medium text-gray-700">
                    Auto-approve new member posts
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="notify-admins"
                    type="checkbox"
                    defaultChecked
                    className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                  />
                  <label htmlFor="notify-admins" className="ml-3 block text-sm font-medium text-gray-700">
                    Notify admins of new posts
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="allow-comments"
                    type="checkbox"
                    defaultChecked
                    className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                  />
                  <label htmlFor="allow-comments" className="ml-3 block text-sm font-medium text-gray-700">
                    Allow comments on discussions
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apn-blue"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-apn-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-apn-blue disabled:opacity-50"
              >
                {savingSettings ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupAdmin;
